/* ============================================================
   Quill&Steel — Stacked scroll panels
   ------------------------------------------------------------
   Each panel is held at the top of the viewport while the next one
   scrolls up over it, so the run of sections deals out like a deck
   of cards rather than passing by.

   Uses the GSAP + ScrollTrigger pair theme.liquid already loads, and
   the same `animations-true` body class the theme's other animated
   sections check, so "Enable animations" still switches it off.

   Progressive enhancement throughout: the markup is untouched and
   unstyled until this script has confirmed it can run. If GSAP is
   missing or an error is thrown, the page is simply a normal stack
   of sections. There is no state here that can hide content.
   ============================================================ */
(function () {
	'use strict';

	var body = document.body;

	if (!body.classList.contains('animations-true')) return;
	if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

	/* Pinning takes the scroll position out of the reader's hands for
	   several screens at a time, which is precisely what someone asking
	   for reduced motion is asking not to happen. */
	if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

	/* Marked per section in the theme editor, on the inner element,
	   because a section cannot put a class on its own Shopify wrapper. */
	var inners = document.querySelectorAll('#main-content .qs-stack-panel');
	if (inners.length < 2) return; /* Nothing stacks against itself. */

	var panels = [];
	for (var i = 0; i < inners.length; i++) {
		var wrapper = inners[i].closest('.shopify-section');
		if (wrapper && panels.indexOf(wrapper) === -1) panels.push(wrapper);
	}
	if (panels.length < 2) return;

	/* A pinned panel is exactly one screen tall, and whatever does not
	   fit inside it is covered by the next panel before it can be read.
	   Narrow screens are where that bites: image-with-text drops from
	   two columns to one, so the content is reliably taller than the
	   viewport and the copy gets cut off mid-scroll. Below this width
	   the sections are left to scroll normally. */
	var wideEnough = window.matchMedia('(min-width: 750px)');

	/* The width rule is a proxy for the real question, so ask that too:
	   does every panel's content actually fit on screen? Long copy, a
	   short laptop or browser zoom can overflow at any width, and a
	   silently clipped panel is worse than no effect at all. Measured
	   before the full-screen class lands, while the sections are still
	   at their natural height. */
	function everyPanelFits() {
		for (var i = 0; i < inners.length; i++) {
			if (inners[i].scrollHeight > window.innerHeight) return false;
		}
		return true;
	}

	var built = [];

	function build() {
		if (built.length) return;
		if (!wideEnough.matches || !everyPanelFits()) return;

		/* Styling is applied here rather than in the stylesheet so the
		   full-screen layout only ever exists when the effect behind it
		   is actually running. */
		panels.forEach(function (panel) {
			panel.classList.add('qs-panel');
		});

		panels.forEach(function (panel, index) {
			var isLast = index === panels.length - 1;

			/* The last panel is deliberately never pinned. Every pin holds
			   its panel for one further screen of scrolling, and for the
			   last one there is no successor panel to fill that screen —
			   whatever follows the run scrolls up over a still-fixed panel
			   instead, and anything less than an opaque full-height section
			   shows the panel straight through it. Leaving it unpinned ends
			   the effect cleanly: it covers its predecessor, then scrolls
			   away like an ordinary section and hands off to whatever is
			   next. */
			if (!isLast) {
				/* pinSpacing: false is what makes them stack. With spacing on,
				   ScrollTrigger inserts a placeholder the height of the pin
				   and the panels queue up one after another instead of
				   overlapping. The default end of 'bottom top' releases each
				   panel after one panel-height of scrolling, which is the
				   moment the next one has finished covering it. */
				built.push(
					ScrollTrigger.create({
						trigger: panel,
						start: 'top top',
						pin: true,
						pinSpacing: false
					})
				);
			}

			if (isLast) return;

			/* Sells the depth: the outgoing panel settles back and dims as
			   its successor rides up over it. Driven off the NEXT panel's
			   position, since that is what the movement is reacting to.
			   Applied to the inner element, never the pinned wrapper. */
			built.push(
				gsap.to(panel.querySelector('.qs-stack-panel'), {
					scale: 0.93,
					opacity: 0.45,
					ease: 'none',
					scrollTrigger: {
						trigger: panels[index + 1],
						start: 'top bottom',
						end: 'top top',
						scrub: true
					}
				})
			);
		});
	}

	function teardown() {
		if (!built.length) return;

		built.forEach(function (item) {
			/* Tweens own their ScrollTrigger, so killing the tween takes
			   the trigger with it; bare triggers are killed directly. */
			if (item.scrollTrigger) item.scrollTrigger.kill(true);
			item.kill(true);
		});
		built = [];

		panels.forEach(function (panel) {
			panel.classList.remove('qs-panel');
			/* kill(true) reverts the pin, but the dim-and-shrink tween
			   leaves its own inline transform and opacity behind. */
			gsap.set(panel.querySelector('.qs-stack-panel'), {
				clearProps: 'transform,opacity,willChange'
			});
		});
	}

	build();

	/* Rotating a phone into landscape, or dragging a desktop window
	   narrow, crosses the threshold with the effect already built — and
	   a stale set of pins is what produces clipped copy. Rebuild against
	   whichever state the viewport is now in. */
	function reassess() {
		if (wideEnough.matches && everyPanelFits()) {
			build();
			ScrollTrigger.refresh();
		} else {
			teardown();
		}
	}

	if (wideEnough.addEventListener) {
		wideEnough.addEventListener('change', reassess);
	} else if (wideEnough.addListener) {
		wideEnough.addListener(reassess); /* Safari < 14 */
	}

	var resizeTimer;
	window.addEventListener('resize', function () {
		clearTimeout(resizeTimer);
		resizeTimer = setTimeout(reassess, 200);
	});

	/* Pinning is measured in pixels, so every offset below the run is
	   wrong until the images above it have settled at their real size.
	   This is also the first point the fit check can trust its numbers. */
	window.addEventListener('load', reassess);
})();
